import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { equipmentLifecycleService } from '../../../lib/equipmentLifecycleService';
import { useAuth } from '../../../contexts/AuthContext';

const BarcodeScanModal = ({ onClose, onScan, equipment }) => {
  const { userProfile } = useAuth();
  const [barcode, setBarcode] = useState('');
  const [scanType, setScanType] = useState('inspection');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [scannedEquipment, setScannedEquipment] = useState(null);

  const scanTypeOptions = [
    { value: 'check_in', label: 'Check In' },
    { value: 'check_out', label: 'Check Out' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'audit', label: 'Audit' }
  ];

  const handleBarcodeSearch = () => {
    const found = equipment?.find(
      (eq) => eq?.barcode?.toLowerCase() === barcode?.toLowerCase() ||
              eq?.serial_number?.toLowerCase() === barcode?.toLowerCase()
    );
    setScannedEquipment(found);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!scannedEquipment) return;

    setLoading(true);
    try {
      const scanData = {
        equipment_id: scannedEquipment?.id,
        scanned_by: userProfile?.id,
        scan_type: scanType,
        location: location || scannedEquipment?.location,
        notes
      };

      const { error } = await equipmentLifecycleService?.createBarcodeScan(scanData);
      if (!error) {
        onScan(scannedEquipment);
      }
    } catch (error) {
      console.error('Error creating barcode scan:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={(e) => e?.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Barcode Scanner</h2>
            <p className="text-sm text-muted-foreground mt-1">Scan or enter equipment barcode</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Barcode Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Barcode / Serial Number *
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="Enter or scan barcode"
                value={barcode}
                onChange={(e) => setBarcode(e?.target?.value)}
                required
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                iconName="Search"
                onClick={handleBarcodeSearch}
              >
                Search
              </Button>
            </div>
          </div>

          {/* Scanned Equipment Display */}
          {scannedEquipment && (
            <div className="bg-success/10 border border-success rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle2" size={20} className="text-success" />
                <h3 className="font-semibold text-foreground">Equipment Found</h3>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  <strong>Name:</strong> {scannedEquipment?.name}
                </p>
                <p className="text-sm text-foreground">
                  <strong>Serial Number:</strong> {scannedEquipment?.serial_number}
                </p>
                <p className="text-sm text-foreground">
                  <strong>Location:</strong> {scannedEquipment?.location}
                </p>
                <p className="text-sm text-foreground">
                  <strong>Status:</strong> {scannedEquipment?.status}
                </p>
              </div>
            </div>
          )}

          {barcode && !scannedEquipment && (
            <div className="bg-error/10 border border-error rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error">Equipment not found. Please check the barcode and try again.</p>
              </div>
            </div>
          )}

          {/* Scan Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Scan Type *
            </label>
            <Select
              options={scanTypeOptions}
              value={scanType}
              onChange={setScanType}
              placeholder="Select scan type"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Location (Optional)
            </label>
            <Input
              type="text"
              placeholder="Enter current location"
              value={location}
              onChange={(e) => setLocation(e?.target?.value)}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes (Optional)
            </label>
            <textarea
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground resize-none"
              rows={3}
              placeholder="Add any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              type="submit"
              variant="primary"
              iconName="Save"
              iconPosition="left"
              disabled={!scannedEquipment || loading}
              loading={loading}
            >
              Record Scan
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BarcodeScanModal;